import type { ConnectionPositionPair } from '@angular/cdk/overlay';
import { NgTemplateOutlet } from '@angular/common';
import type { OnChanges, OnInit, SimpleChanges } from '@angular/core';
import { Component, inject, Input, ViewChild } from '@angular/core';

import type { CountryCode } from 'libphonenumber-js';
import { AsYouType, parsePhoneNumber } from 'libphonenumber-js';

import type { Country } from '@core/locations-types';

import type { CountrySelectOption } from './phone-input.types';
import { PHONE_INPUT_DATA } from './phone-input.types';
import { ButtonComponent } from '../common/button/components/button/button.component';
import { ButtonDropdownIndicatorDirective } from '../common/button/directives/button-dropdown/button-dropdown-indicator.directive';
import { IconComponent } from '../common/icon/icon.component';
import { TruncateComponent } from '../common/truncate/truncate.component';
import { InputComponent } from '../forms/input/input.component';
import type { SelectOption } from '../forms/select/select.component';
import { SelectComponent } from '../forms/select/select.component';
import { RtInput } from '../utils/rt-input';

@Component({
  selector: 'rt-phone-input',
  templateUrl: 'phone-input.component.html',
  styleUrls: ['./phone-input.component.less'],
  imports: [
    InputComponent,
    ButtonComponent,
    ButtonDropdownIndicatorDirective,
    IconComponent,
    SelectComponent,
    NgTemplateOutlet,
    TruncateComponent,
  ],
})
export class PhoneInputComponent extends RtInput<string> implements OnInit, OnChanges {
  @Input() public placeholder: string;
  @Input() public countryCode: string | null;

  @ViewChild('phoneInput', { static: true }) public inputComponent: InputComponent;

  public value: string;
  public options: CountrySelectOption[] = [];
  public selectedCountry: Country | null;
  public dropdownPositions: ConnectionPositionPair[] = [
    { originX: 'start', originY: 'bottom', overlayX: 'start', overlayY: 'top' },
  ];

  private allCountries: Country[] = inject(PHONE_INPUT_DATA);
  private touched: boolean = false;

  public searchStrategy = (item: SelectOption<string>, searchQuery: string) => {
    const parsedItemLabel = item.label
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .toLowerCase();
    const parsedSearchQuery = searchQuery
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .toLowerCase()
      .trim();
    return parsedItemLabel.includes(parsedSearchQuery);
  };

  // eslint-disable-next-line @typescript-eslint/no-empty-function
  public onTouched = () => {};
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  public onChange = (number: string) => {};

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes.countryCode?.currentValue && this.allCountries.length) this.init();
  }

  public ngOnInit() {
    this.options = this.prepareSelectItems();
  }

  public registerOnChange(onChange: any): void {
    this.onChange = onChange;
  }

  public registerOnTouched(fn: any) {
    this.onTouched = fn;
  }

  public writeValue(value: string): void {
    this.value = value;
    this.init();
    this.cdRef.detectChanges();
  }

  public init(): void {
    if (this.value) {
      this.markAsTouched();
      const asYouType = new AsYouType();
      asYouType.input(this.value);
      this.inputComponent.getNativeInputElement().value = this.value;
      const countryCodeFromNumber = asYouType.getCountry();
      const selectedCountry = this.resolveCountry(countryCodeFromNumber ?? null);
      this.setSelectedCountry(selectedCountry);
      const formattedNumber = this.formatNumber(this.value);
      this.onChange(formattedNumber);
    } else {
      const selectedCountry = this.resolveCountry();
      this.onCountrySelect(selectedCountry?.code);
    }
    this.ngControl?.control?.markAsPristine();
  }

  public getCountryFlag(countryCode: string): string {
    return 'iti__' + countryCode.toLowerCase();
  }

  public setFocus(): void {
    if (!this.inputComponent) return;
    this.inputComponent.setFocus();
  }

  public onInput(number: string): void {
    const asYouType = new AsYouType();
    asYouType.input(number);
    this.inputComponent.getNativeInputElement().value = number;
    this.markAsTouched();
    const countryCodeFromNumber = asYouType.getCountry();
    const newCountryCode = this.resolveCountry(countryCodeFromNumber ?? null);
    this.setSelectedCountry(newCountryCode);
    const formattedNumber = this.formatNumber(number);
    this.value = formattedNumber;
    this.onChange(formattedNumber);
    this.cdRef.detectChanges();
  }

  public onKeydown(e: KeyboardEvent): void {
    // allows only to press numbers and specific buttons
    if (
      [46, 8, 9, 27, 13, 110, 190].indexOf(e.keyCode) !== -1 ||
      (e.key === 'a' && (e.ctrlKey || e.metaKey)) ||
      (e.key === 'c' && (e.ctrlKey || e.metaKey)) ||
      (e.key === 'v' && (e.ctrlKey || e.metaKey)) ||
      (e.key === 'x' && (e.ctrlKey || e.metaKey)) ||
      (e.keyCode >= 35 && e.keyCode <= 39)
    ) {
      return;
    }
    const regEx = new RegExp(`^[+]*[0-9]*$`);
    if (regEx.test(e.key)) return;
    else e.preventDefault();
  }

  public onCountrySelect(code: CountryCode | string | undefined): void {
    const country = this.allCountries.find(c => c.code === code);
    if (!country) return;
    let number;
    if (this.value) {
      try {
        const oldNumber = parsePhoneNumber(this.value).nationalNumber;
        number = parsePhoneNumber(oldNumber, <CountryCode>country.code).number;
      } catch (e) {
        number = `+${country.countryCode}` + this.value;
      }
    } else {
      number = `+${country.countryCode}`;
    }
    this.inputComponent.getNativeInputElement().value = number;
    this.setSelectedCountry(country);
    const formattedNumber = this.formatNumber(number);
    this.value = formattedNumber;
    this.onChange(formattedNumber);
  }

  private getDefaultCountry(countryCode: any): Country | null {
    if (countryCode) return this.allCountries.find(country => country.code === countryCode) || null;

    return this.allCountries.find(country => country.code === 'NL') || this.allCountries[0];
  }

  private formatNumber(number: string): string {
    return number === `+${this.selectedCountry?.countryCode}` ? '' : number;
  }

  private prepareSelectItems(): CountrySelectOption[] {
    return this.allCountries.map(country => ({
      label: country.name,
      value: country.code,
      countryCode: country.countryCode,
    }));
  }

  private setSelectedCountry(country: Country | null): void {
    this.selectedCountry = country;
    this.cdRef.detectChanges();
  }

  private resolveCountry(countryCodeFromNumber?: CountryCode | null): Country | null {
    let country;
    if (countryCodeFromNumber === null) return null;
    if (countryCodeFromNumber) {
      country =
        this.allCountries.find(country => country.code === <string>countryCodeFromNumber) || null;
    } else {
      country = this.getDefaultCountry(this.countryCode);
    }
    return country;
  }

  private markAsTouched() {
    if (!this.touched) {
      this.onTouched();
      this.touched = true;
      this.cdRef.detectChanges();
    }
  }
}
